import { Button } from "@/src/components/buttons";
import { closeDrawer, openDrawer } from "@/src/components/dialogs";
import { Input } from "@/src/components/universalInput";
import { UploadCards, UploadSpots } from "@/src/components/uploadSpots";
import { Avis, Hospitalisation, Patient, User } from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import {
  getItemId,
  getUserId,
  getUserShortName,
  jsonAbstract,
} from "@/src/utils/utils";
import React from "react";
import { UsersCheckboxList } from "../admin/users/usersHook";
import { sendSimpleNotification } from "../notifications/notificationsHook";
import { InterventionsCompartment } from "./historique/interventionsCompartment";
import { openHospitalisationDrawer } from "./hospitalisationHook";

export async function openAvisForm({
  patient,
  avis,
  onSave,
}: {
  patient: Patient;
  avis: Avis;
  onSave: (avis: Avis) => void;
}) {
  alert();
  await openDrawer({
    id: "avisDrawer",
    title: "Avis",
    additionalClasses: ["small"],

    content: (
      <AvisForm
        patient={patient}
        avis={avis}
        onChange={(newValue) => {
          avis = newValue;
        }}
      ></AvisForm>
    ),
    onConfirm: async () => {
      if (!avis.isDemand) {
        avis.doctor = getUserId();
        avis.status = "finished";

        let result = await apiFetch("avis", "addOne", { avis });
        if (!result) throw new Error("Error while adding avis");

        onSave?.(avis);

        closeDrawer("avisDrawer");
        return;
      }

      if (avis.isDemand && avis.status != "finished") {
        let targetUsers: User[] = [];
        await openDrawer({
          id: "usersCheckboxList",
          title: "Choisir les médecins à notifier",
          content: (
            <UsersCheckboxList
              serviceId={avis.service?.id}
              selectedUsers={targetUsers}
              onChange={(newUsers) => {
                targetUsers = newUsers;
              }}
            ></UsersCheckboxList>
          ),
          onConfirm: async () => {
            let result = await apiFetch("avis", "addOne", { avis });
            if (!result) throw new Error("Error while adding avis");

            onSave?.(avis);

            closeDrawer("avisDrawer");

            await sendSimpleNotification({
              item: result,
              type: "avisDemand",
              serviceId: avis.service?.id,
              targetUsers: jsonAbstract(targetUsers.map((u) => u.id)),
            });
          },
        });
      }
    },
  });
}

function AvisForm({
  patient,
  avis,
  onChange,
}: {
  patient: Patient;
  avis: Avis;
  onChange?: (newValue: Avis) => void;
}) {
  const [current, setCurrent] = React.useState(avis);
  function onLocalChange(newValue: Avis) {
    setCurrent(newValue);
    onChange?.(newValue);
  }
  console.log("avis.parent", avis.parent);
  return (
    <div className="c1 gap15">
      <Input
        type="chips"
        name={""}
        value={current.isDemand?.toString()}
        options={[
          { value: "true", label: "Envoyer demande" },
          { value: "false", label: "Rédiger" },
        ]}
        onChange={(newValue) => {
          onLocalChange({ ...current, isDemand: newValue == "true" });
        }}
      ></Input>

      {current.isDemand && (
        <>
          <Input
            name={"Demande"}
            value={current.demandNotes}
            type="textarea"
            rows={10}
            placeholder="Champs de texte libre..."
            onChange={(newValue) => {
              onLocalChange({ ...current, demandNotes: newValue });
            }}
          />
          <div></div>
          <UploadSpots
            name="Pièces jointes"
            value={current.demandUploads}
            gridCount={2}
            spotsCount={2}
            additionalUploadData={{
              patient: getItemId(patient),
            }}
            onChange={(newValue) => {
              onLocalChange({
                ...current,
                demandUploads: newValue,
              });
            }}
          ></UploadSpots>
        </>
      )}
      {!current.isDemand && (
        <>
          <Input
            name={"Notes et remarques"}
            value={current.notes}
            type="textarea"
            rows={10}
            placeholder="Champs de texte libre..."
            onChange={(newValue) => {
              onLocalChange({ ...current, notes: newValue });
            }}
          />
          <Input
            name="Conclusion"
            value={current.conclusion}
            type="textarea"
            rows={4}
            placeholder="Champs de texte libre..."
            onChange={(newValue) => {
              onLocalChange({ ...current, conclusion: newValue });
            }}
          />
          <div></div>
          <UploadSpots
            name="Pièces jointes"
            value={current.uploads}
            gridCount={2}
            spotsCount={2}
            additionalUploadData={{
              patient: getItemId(patient),
            }}
            onChange={(newValue) => {
              onLocalChange({
                ...current,
                uploads: newValue,
              });
            }}
          ></UploadSpots>

          {avis.parent != null && (
            <InterventionsCompartment
              patient={patient}
              parent={avis.parent as Hospitalisation}
            ></InterventionsCompartment>
          )}
        </>
      )}
    </div>
  );
}

export async function openAvisAnswerForm(
  patient: Patient,
  avis: Avis,
  onConfirm: (avis: Avis) => void
) {
  await openDrawer({
    id: "avisAnswerDrawer",
    title: "Avis",
    additionalClasses: ["small"],

    content: (
      <AvisAnswerForm
        patient={patient}
        avis={avis}
        onChange={(newValue) => {
          avis = newValue;
        }}
      ></AvisAnswerForm>
    ),
    trailing: (
      <div>
        <Button
          onClick={async () => {
            await openHospitalisationDrawer({
              itemId: getItemId(avis.parent),
              size: "big",
              onImmediateDischarge: () => {},
              onConfirm: (hospitalisation) => {
                closeDrawer("avisAnswerDrawer");
              },
            });
          }}
        >
          Dossier complet
        </Button>
      </div>
    ),
    onConfirm: async () => {
      avis.doctor = USER.get();
      avis.status = "finished";

      let result = await apiFetch("avis", "addOne", { avis });
      if (!result) return;

      sendSimpleNotification({
        type: "avisAnswer",
        item: jsonAbstract(result.id),
        targetUsers: [getItemId(avis.creator)],
      });

      onConfirm?.(avis);

      closeDrawer("avisAnswerDrawer");
    },
  });
}

function AvisAnswerForm({
  patient,
  avis,
  onChange,
}: {
  patient: Patient;
  avis: Avis;
  onChange?: (newValue: Avis) => void;
}) {
  const [current, setCurrent] = React.useState(avis);
  function onLocalChange(newValue: Avis) {
    setCurrent(newValue);
    onChange?.(newValue);
  }
  console.log("avis.parent", avis.parent);
  return (
    <div className="c1 gap15">
      <div className="grid c1 gap10 blueish br p10">
        <div className="bold">Demandé par {getUserShortName(avis.creator)}</div>
        <div>{avis.demandNotes}</div>
        <UploadCards uploads={avis.demandUploads} count={2} />
      </div>

      <Input
        name={"Notes et remarques"}
        value={current.notes}
        type="textarea"
        rows={10}
        placeholder="Champs de texte libre..."
        onChange={(newValue) => {
          onLocalChange({ ...current, notes: newValue });
        }}
      />
      <Input
        name="Conclusion"
        value={current.conclusion}
        type="textarea"
        rows={4}
        placeholder="Champs de texte libre..."
        onChange={(newValue) => {
          onLocalChange({ ...current, conclusion: newValue });
        }}
      />
      <div></div>
      <UploadSpots
        name="Pièces jointes"
        value={current.uploads}
        gridCount={2}
        spotsCount={2}
        additionalUploadData={{
          patient: getItemId(patient),
        }}
        onChange={(newValue) => {
          onLocalChange({
            ...current,
            uploads: newValue,
          });
        }}
      ></UploadSpots>

      {avis.parent != null && (
        <InterventionsCompartment
          patient={patient}
          parent={avis.parent as Hospitalisation}
        ></InterventionsCompartment>
      )}
    </div>
  );
}
